/**
 * The shell: providers, navigation, the route switch, the update prompt.
 *
 * Screens live in src/screens/<Name>/index.tsx and are lazy-loaded (one chunk each, all
 * precached by the service worker). Adding a screen: add the route to router.ts, a lazy
 * import below, and a case in `Screen`. Screens never edit this file otherwise.
 */
import type { ReactNode } from 'react'
import { Suspense, lazy, useEffect, useMemo } from 'react'
import type { Route } from './router'
import { formatRoute, navigate, useRoute } from './router'
import { AppNav } from './shell/AppNav'
import { applyTheme } from './shell/theme'
import type { Store } from './store'
import { StoreProvider, getStore, useSettings, useStore, useStoreStatus } from './store'
import { SyncProvider } from './sync'
import {
  Button,
  EmptyState,
  ErrorBoundary,
  Page,
  Spinner,
  ToastProvider,
  UnitsProvider,
  useToast,
} from './ui'

const TripsScreen = lazy(() => import('./screens/Trips').then((m) => ({ default: m.TripsScreen })))
const TripScreen = lazy(() => import('./screens/Trip').then((m) => ({ default: m.TripScreen })))
const PlanScreen = lazy(() => import('./screens/Plan').then((m) => ({ default: m.PlanScreen })))
const ShoppingListScreen = lazy(() =>
  import('./screens/ShoppingList').then((m) => ({ default: m.ShoppingListScreen })),
)
const ShopScreen = lazy(() => import('./screens/Shop').then((m) => ({ default: m.ShopScreen })))
const RecipesScreen = lazy(() =>
  import('./screens/Recipes').then((m) => ({ default: m.RecipesScreen })),
)
const RecipeScreen = lazy(() =>
  import('./screens/Recipe').then((m) => ({ default: m.RecipeScreen })),
)
const FoodsScreen = lazy(() => import('./screens/Foods').then((m) => ({ default: m.FoodsScreen })))
const FoodScreen = lazy(() => import('./screens/Food').then((m) => ({ default: m.FoodScreen })))
const FoodImportScreen = lazy(() =>
  import('./screens/FoodImport').then((m) => ({ default: m.FoodImportScreen })),
)
const AvailabilityScreen = lazy(() =>
  import('./screens/Availability').then((m) => ({ default: m.AvailabilityScreen })),
)
const SettingsScreen = lazy(() =>
  import('./screens/Settings').then((m) => ({ default: m.SettingsScreen })),
)

/** The route to a screen element. Exported for tests. */
export function Screen({ route }: { route: Route }) {
  const query = route.query ?? {}
  switch (route.name) {
    case 'trips':
      return <TripsScreen query={query} />
    case 'trip':
      return <TripScreen tripId={route.tripId} query={query} />
    case 'plan':
      return <PlanScreen tripId={route.tripId} sectionId={route.sectionId} query={query} />
    case 'list':
      return <ShoppingListScreen tripId={route.tripId} sectionId={route.sectionId} query={query} />
    case 'shop':
      return <ShopScreen tripId={undefined} sectionId={undefined} query={query} />
    case 'shop-section':
      return <ShopScreen tripId={route.tripId} sectionId={route.sectionId} query={query} />
    case 'recipes':
      return <RecipesScreen query={query} />
    case 'recipe':
      return <RecipeScreen id={route.id} query={query} />
    case 'foods':
      return <FoodsScreen query={query} />
    case 'food':
      return <FoodScreen id={route.id} query={query} />
    case 'food-import':
      return <FoodImportScreen query={query} />
    case 'availability':
      return <AvailabilityScreen query={query} />
    case 'settings':
      return <SettingsScreen query={query} />
    case 'not-found':
      return (
        <Page title="Not found">
          <EmptyState
            title="Nothing here"
            description={`There is no page at ${route.path}.`}
            action={
              <Button variant="primary" onClick={() => navigate({ name: 'trips' })}>
                Go to trips
              </Button>
            }
          />
        </Page>
      )
  }
}

/** Settings that act on the whole document: theme, units, failed-write notices. */
function Effects() {
  const { resolved } = useSettings()
  const store = useStore()
  const toast = useToast()
  const { ready } = useStoreStatus()

  useEffect(() => {
    // Until settings load, main.tsx's early theme (from the localStorage mirror) stands.
    if (!ready) return
    applyTheme(resolved.theme)
    if (resolved.theme !== 'system' || !window.matchMedia) return
    const q = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyTheme('system')
    q.addEventListener('change', onChange)
    return () => q.removeEventListener('change', onChange)
  }, [ready, resolved.theme])

  useEffect(
    () =>
      store.onError((err) =>
        toast.show(`Could not save that change: ${err.message}`, { tone: 'error', duration: 0 }),
      ),
    [store, toast],
  )
  return null
}

function NotSavedWarning() {
  const toast = useToast()
  useEffect(() => {
    const id = toast.show(
      'This browser is not letting Resupport save anything. Export from Settings before you close the tab.',
      { tone: 'warn', duration: 0 },
    )
    return () => toast.dismiss(id)
  }, [toast])
  return null
}

function Shell({ children }: { children?: ReactNode }) {
  const route = useRoute()
  const { ready, error, persistent } = useStoreStatus()
  const { resolved } = useSettings()
  const units = useMemo(
    () => ({
      energy: resolved.energy_unit,
      currency: resolved.currency,
      currencyDisplay: resolved.currency_display,
      mass: resolved.mass_unit,
    }),
    [resolved.energy_unit, resolved.currency, resolved.currency_display, resolved.mass_unit],
  )

  // Land somewhere real rather than on a bare "#".
  useEffect(() => {
    if (!window.location.hash) navigate({ name: 'trips' }, { replace: true })
  }, [])

  // Each route starts at the top.
  const key = formatRoute({ ...route, query: undefined } as Route)
  useEffect(() => {
    const page = document.scrollingElement ?? document.documentElement
    page.scrollTop = 0
  }, [key])

  return (
    <UnitsProvider value={units}>
      <Effects />
      <div className="rs-app">
        <AppNav route={route} />
        <main className="rs-main" id="main">
          <ErrorBoundary resetKey={key}>
            {error ? (
              <FatalError error={error} />
            ) : ready ? (
              <Suspense
                fallback={
                  <div className="rs-centre">
                    <Spinner size="lg" />
                  </div>
                }
              >
                <Screen route={route} />
              </Suspense>
            ) : (
              <div className="rs-centre">
                <Spinner size="lg" label="Opening your food database" />
              </div>
            )}
          </ErrorBoundary>
        </main>
      </div>
      {children}
      {ready && !persistent ? <NotSavedWarning /> : null}
    </UnitsProvider>
  )
}

/**
 * `store` is injected by tests; the app uses the IndexedDB singleton. `children` render inside
 * every provider: main.tsx passes the service-worker UpdatePrompt this way, which keeps the
 * `virtual:pwa-register` import out of anything a test imports.
 */
export default function App({ store, children }: { store?: Store; children?: ReactNode }) {
  const s = useMemo(() => store ?? getStore(), [store])
  return (
    <StoreProvider store={s}>
      <ToastProvider>
        <SyncProvider autoStart={!store}>
          <Shell>{children}</Shell>
        </SyncProvider>
      </ToastProvider>
    </StoreProvider>
  )
}

/** Exported for main.tsx's last-resort error screen. */
export function FatalError({ error }: { error: Error }) {
  return (
    <div className="rs-boundary" role="alert">
      <h2>Resupport could not start</h2>
      <pre>{error.message}</pre>
      <Button variant="primary" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  )
}
